import html2canvas from "html2canvas";
import { LAYOUTS } from "../components/layouts";
import designerConfig from "./designerConfig.json";
import { getMetalRates } from "../services/metalRates";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

// Theme / font helpers
function getThemeConfig(themeId) {
  const found = designerConfig.themes.find((t) => t.id === themeId);
  if (found) return found;
  try {
    const raw = localStorage.getItem("jmi-custom-themes");
    if (raw) {
      const customs = JSON.parse(raw);
      const custom = customs.find((t) => t.id === themeId);
      if (custom) return custom;
    }
  } catch (_) {}
  return designerConfig.themes.find((t) => t.id === "light-studio") || designerConfig.themes[0];
}

function getFontPairConfig(fontPairId) {
  const found = designerConfig.fontPairs.find((f) => f.id === fontPairId);
  if (found) return found;
  return designerConfig.fontPairs.find((f) => f.id === "modern-clean") || designerConfig.fontPairs[0];
}

function buildCssVars(themeConfig, fontConfig) {
  const c = themeConfig.colors || themeConfig;
  return {
    "--theme-bg": c.bg || "#FFFFFF",
    "--theme-surface": c.surface || "#F8F9FA",
    "--theme-text": c.text || "#0F172A",
    "--theme-textMuted": c.textMuted || "#64748B",
    "--theme-border": c.border || "#E2E8F0",
    "--theme-accent": c.accent || "#D4AF37",
    "--theme-primary": c.primary || "#0F172A",
    "--theme-heading-font": `"${fontConfig.heading || "Montserrat"}", sans-serif`,
    "--theme-body-font": `"${fontConfig.body || "Open Sans"}", sans-serif`,
  };
}

async function computeRetailPrice(product, buyer, wholesaleMakingAmount) {
  const seg = (product?.segment || "gold").toLowerCase();
  const marginPct = Number(buyer?.cardDesign?.metalValues?.[seg]?.percentage) || Number(buyer?.cardDesign?.metalValues?.gold?.percentage) || 5;
  const num = (v) => parseFloat(v) || 0;
  const avgNetWt = num(product?.lotDetails?.[0]?.avgNetWt || product?.avgNetWt || 0);
  const purityPct = num(product?.buyerDeskCalculation || product?.purity || 0);
  const wastagePct = num(product.wastage) + num(product.wastageOperator) + num(product.wastageJmi);
  const fineWeight = avgNetWt > 0 ? (avgNetWt * (purityPct + wastagePct)) / 100 : 0;

  try {
    const rates = await getMetalRates({ useLiveApi: true });
    if (rates) {
      const ratePerGram = rates[seg]?.local || rates[seg]?.india || rates.gold?.local || 0;
      if (ratePerGram > 0 && fineWeight > 0) {
        const metalValue = fineWeight * ratePerGram * (1 + marginPct / 100);
        return Math.round(metalValue + (wholesaleMakingAmount || 0));
      }
    }
  } catch (_) {}

  const baked = Number(product?.pricing?.retail) || Number(product?.attributes?.retail_price) || 0;
  if (baked > 0) return baked;
  return Math.round((wholesaleMakingAmount || 0) * (1 + marginPct / 100));
}

function waitForImages(container, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const imgs = Array.from(container.querySelectorAll("img"));
    if (!imgs.length) { resolve(); return; }
    let pending = imgs.length;
    const done = () => { if (--pending === 0) resolve(); };
    const timer = setTimeout(resolve, timeoutMs);
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        done();
      } else {
        img.onload = () => { done(); };
        img.onerror = () => { done(); };
      }
    });
    if (pending === 0) { clearTimeout(timer); resolve(); }
  });
}

// Add JMI watermark to bottom left corner
function addJMIWatermark(sourceCanvas) {
  const newCanvas = document.createElement('canvas');
  newCanvas.width = sourceCanvas.width;
  newCanvas.height = sourceCanvas.height;
  const ctx = newCanvas.getContext('2d');
  
  ctx.drawImage(sourceCanvas, 0, 0);
  
  // 2px black watermark
  const fontSize = 10;
  ctx.fillStyle = '#000000'; // Black
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  
  const padding = 2;
  const x = padding;
  const y = newCanvas.height - padding;
  
  ctx.fillText('JMI', x, y);
  return newCanvas;
}

// Main download function
export const downloadProductPoster = async (
  product,
  buyer,
  wholesaleMakingAmt = 0,
  overrides = {},
) => {
  const { fileName = `${product.productName || product.name || "product"}-poster`, format = "png", quality = 0.95 } = overrides;

  try {
    console.log("[posterDownload] Starting download...");

    const seg = (product?.segment || "gold").toLowerCase();
    const savedDesign = buyer?.posterDesigns?.[seg] || buyer?.cardDesign || {};
    const layoutKey = savedDesign.activeLayout || "editorial-catalogue";
    const themeId = savedDesign.activeTheme || "light-studio";
    const fontId = savedDesign.activeFontPair || "modern-clean";
    const visibleFields = savedDesign.visibleFields || { logoUrl: true, organizationName: true, address: true, phone: true };

    const themeConfig = getThemeConfig(themeId);
    const fontConfig = getFontPairConfig(fontId);
    const cssVars = buildCssVars(themeConfig, fontConfig);
    const LayoutComponent = LAYOUTS[layoutKey] || LAYOUTS["editorial-catalogue"];
    const bgColor = (themeConfig.colors || themeConfig).bg || "#FFFFFF";

    const retailPrice = await computeRetailPrice(product, buyer, wholesaleMakingAmt);
    const marginPct = Number(buyer?.cardDesign?.metalValues?.[seg]?.percentage) || Number(buyer?.cardDesign?.metalValues?.gold?.percentage) || 5;

    const imageUrls = (() => {
      if (product.image_url) return Array.isArray(product.image_url) ? product.image_url : [product.image_url];
      if (Array.isArray(product.images) && product.images.length) return product.images.map((i) => (typeof i === "string" ? i : i.url || "")).filter(Boolean);
      return [];
    })();

    const avgGrossWt = product?.lotDetails?.[0]?.avgGrossWt || product?.grossWt || "";
    const productForPoster = {
      ...product,
      name: product.name || product.productName || "",
      image_url: imageUrls,
      pricing: { ...(product.pricing || {}), retail: retailPrice, retail_margin_percent: marginPct },
      attributes: {
        ...(product.attributes || {}),
        Segment: product.segment || "",
        Category: product.category || "",
        "Weight": avgGrossWt ? `${parseFloat(avgGrossWt).toFixed(3)} g` : "",
        Purity: product.purity ? `${product.purity}%` : (product.buyerDeskCalculation ? `${product.buyerDeskCalculation}%` : ""),
        ...(product.specification && product.specification !== "PLANE" ? { Specification: product.specification } : {}),
        Style: product.styleType || product.finishType || "",
        retail_price: retailPrice,
      },
    };

    Object.keys(productForPoster.attributes).forEach((k) => {
      const v = productForPoster.attributes[k];
      if (v === undefined || v === null || v === "") delete productForPoster.attributes[k];
    });

    // Create off-screen container - NO FIXED SIZE
    // Let it render naturally based on content
    const container = document.createElement("div");
    container.setAttribute("data-poster-root", "true");
    container.style.cssText = "position: fixed; left: -9999px; top: 0; overflow: visible; z-index: -1; margin: 0; padding: 0; border: none; box-sizing: border-box; display: flex; flex-direction: column;";
    container.style.background = bgColor;
    Object.entries(cssVars).forEach(([k, v]) => container.style.setProperty(k, v));
    document.body.appendChild(container);

    console.log("[posterDownload] Container created, rendering layout...");

    // Render layout
    const React = await import("react");
    const ReactDOM = await import("react-dom/client");
    const root = ReactDOM.createRoot(container);

    root.render(
      React.createElement(LayoutComponent, {
        product: productForPoster,
        buyer,
        cardScale: 100,
        visibleFields,
        cssVars,
        forExport: true,
      })
    );

    console.log("[posterDownload] Layout rendered, waiting for images...");

    // Wait for render and images
    await new Promise((resolve) => {
      setTimeout(async () => {
        await waitForImages(container);
        setTimeout(resolve, 500);
      }, 150);
    });

    console.log("[posterDownload] Images loaded, capturing with html2canvas...");

    // Capture with html2canvas - NO FIXED SIZE, capture natural content
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: bgColor,
      logging: false,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        const cloned = clonedDoc.querySelector("[data-poster-root]");
        if (cloned) {
          cloned.style.position = "relative";
          cloned.style.left = "0px";
          cloned.style.overflow = "visible";
        }
      },
    });

    console.log("[posterDownload] Canvas captured, cleaning up...");

    // Cleanup
    root.unmount();
    document.body.removeChild(container);

    // Add watermark
    console.log("[posterDownload] Adding watermark...");
    const watermarkedCanvas = addJMIWatermark(canvas);

    // Convert to data URL
    const dataUrl = watermarkedCanvas.toDataURL(`image/${format}`, quality);

    console.log("[posterDownload] Data URL created, initiating download...");

    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
      try {
        const base64Data = dataUrl.split(',')[1];
        const savedFile = await Filesystem.writeFile({
          path: `${fileName}.${format}`,
          data: base64Data,
          directory: Directory.Cache,
        });

        await Share.share({
          title: 'Share Poster',
          text: `${product.productName || product.name} - JMI Poster`,
          url: savedFile.uri,
          dialogTitle: 'Share your poster',
        });

        console.log("[posterDownload] Native download successful");
        return { success: true, dataUrl, platform: 'native' };
      } catch (error) {
        console.error("[posterDownload] Native share failed:", error);
      }
    }

    // Web download
    const link = document.createElement("a");
    link.download = `${fileName}.${format}`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("[posterDownload] Web download successful");
    return { success: true, dataUrl, platform: 'web' };
  } catch (error) {
    console.error("[posterDownload] Full error:", error);
    console.error("[posterDownload] Error stack:", error.stack);

    try {
      const orphan = document.querySelector("[data-poster-root]");
      if (orphan) document.body.removeChild(orphan);
    } catch (_) {}

    throw error;
  }
};

const posterDownload = { downloadProductPoster };
export default posterDownload;
